import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { collection, getDocs, query, where, doc, setDoc } from "firebase/firestore";
import { db } from "@/config/firebase";

const SEASON = "2026-27";

// Print sheets: page 1 = B/W presentation image, page 2 = "Warmer Regen" (duplex, long edge)
export default function WarmRainPrint() {
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [pres, setPres] = useState({});

  useEffect(() => {
    (async () => {
      const [wr, us, pr] = await Promise.all([
        getDocs(collection(db, "warm_rain")),
        getDocs(collection(db, "users")),
        getDocs(query(collection(db, "prediction_presentations"), where("seasonId", "==", SEASON))),
      ]);
      const pm = {}; pr.docs.forEach(d => { pm[d.data().userId] = d.data(); });
      setPres(pm);
      setUsers(us.docs.map(d => ({ id: d.id, name: d.data().display_name || d.data().name || d.id, photoUrl: d.data().photo_url || '' })).sort((a, b) => a.name.localeCompare(b.name, 'de')));
      setCards(wr.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => (a.userDisplayName || a.sheetName).localeCompare(b.userDisplayName || b.sheetName, 'de')));
    })();
  }, []);

  const remap = async (card, userId) => {
    const u = users.find(x => x.id === userId);
    await setDoc(doc(db, "warm_rain", card.id), { userId: userId || null, userDisplayName: u?.name || null }, { merge: true });
    setCards(prev => prev.map(c => c.id === card.id ? { ...c, userId: userId || null, userDisplayName: u?.name || null } : c));
  };

  return (
    <div className="bg-white text-black min-h-screen" data-testid="warm-rain-print">
      <style>{`
        @page { size: A4 portrait; margin: 0; }
        @media print { html, body { margin: 0 !important; padding: 0 !important; } .no-print { display: none !important; } .sheet { page-break-after: always; break-after: page; margin: 0 !important; height: 297mm !important; max-height: 297mm !important; } .sheet:last-child { page-break-after: auto; break-after: auto; } }
        .sheet { width: 210mm; height: 297mm; max-height: 297mm; box-sizing: border-box; padding: 14mm 16mm 12mm; overflow: hidden; display: flex; flex-direction: column; margin: 0 auto 10mm; }
      `}</style>
      <div className="no-print sticky top-0 z-10 bg-neutral-900 text-white px-6 py-3 flex items-center gap-4 text-sm">
        <span className="font-bold">Druck: Spielerkarten „Warmer Regen“ · {cards.length} Spieler</span>
        <span className="text-white/60">Tipp: Einseitig, A4 hoch, Ränder „Standard“, „Hintergrundgrafiken“ an. Eine Seite pro Spieler.</span>
        <button data-testid="print-btn" onClick={() => window.print()} className="ml-auto px-4 py-1.5 rounded bg-amber-500 text-black font-bold">🖨 Drucken</button>
      </div>

      {cards.map(card => (
        <PrintCard key={card.id} card={card} img={pres[card.userId]?.backgroundImageUrl} photoUrl={users.find(u => u.id === card.userId)?.photoUrl} users={users} remap={remap} />
      ))}
    </div>
  );
}

// One A4 sheet; font auto-shrinks until the content fits the page.
function PrintCard({ card, img, photoUrl, users, remap }) {
  const ref = useRef(null);
  const name = card.userDisplayName || card.sheetName;
  const n = card.entries.length;
  const today = new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const chars = card.entries.join(' ').length;
  const startPx = chars < 400 ? 26 : chars < 700 ? 22 : chars < 1000 ? 19 : chars < 1400 ? 16.5 : 14;
  const cols = chars > 1000 || n > 14 ? 2 : 1;
  const imgH = chars > 1400 ? '80mm' : chars > 900 ? '95mm' : '110mm';
  const [fontPx, setFontPx] = useState(startPx);
  const [tick, setTick] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollHeight > el.clientHeight + 2 && fontPx > 10) setFontPx(f => Math.max(10, f - 0.5));
  }, [fontPx, tick, img]);

  return (
    <section ref={ref} className="sheet" data-testid={`card-${card.id}`} data-font={fontPx}>
      <div className="no-print mb-2 text-xs text-neutral-500 flex items-center gap-2 self-center">
        Sheet: „{card.sheetName}“ → Zuordnung:
        <select value={card.userId || ''} onChange={e => remap(card, e.target.value)} className="border px-1 py-0.5 rounded" data-testid={`map-${card.id}`}>
          <option value="">– kein Spieler –</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
      </div>
      <div className="flex flex-col items-center text-center">
        {img ? (
          <img src={img} alt={name} onLoad={() => setTick(t => t + 1)} className="w-full object-contain" style={{ maxHeight: imgH, filter: 'grayscale(100%) contrast(1.1)' }} data-testid={`img-${card.id}`} />
        ) : (
          <div className="w-1/2 flex items-center justify-center border-4 border-black" style={{ height: 110 }} data-testid={`initials-${card.id}`}>
            <span className="text-6xl font-black">{(name || '?').split(' ').map(w => w[0]).join('').slice(0, 3)}</span>
          </div>
        )}
        {photoUrl && (
          <div
            className="rounded-full overflow-hidden bg-white border-4 border-black flex items-center justify-center"
            style={{ width: '40mm', height: '40mm', marginTop: '-24mm', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}
            data-testid={`avatar-${card.id}`}
          >
            <img src={photoUrl} alt={name} className="w-full h-full object-cover object-top" style={{ filter: 'grayscale(100%) contrast(1.05)' }} />
          </div>
        )}
        <h1 className="mt-3 text-4xl font-black uppercase tracking-wide leading-none">{name}</h1>
        <p className="mt-1 text-xs tracking-[0.35em] uppercase text-neutral-600">Warmer Regen von deinem Team</p>
      </div>
      <div className="mt-4 border-t-2 border-black pt-4 flex-1 flex flex-col justify-center">
        <ul className="list-none text-center" style={{ columnCount: cols, columnGap: '32px', fontSize: fontPx, lineHeight: 1.35 }} data-testid={`entries-${card.id}`}>
          {card.entries.map((e, i) => (
            <li key={i} className="break-inside-avoid px-2" style={{ marginBottom: fontPx * 0.6 }}>„{e}“</li>
          ))}
        </ul>
      </div>
      <p className="mt-2 text-[10px] text-neutral-400 text-center" data-testid={`date-${card.id}`}>{today}</p>
    </section>
  );
}
